import { useState } from "react";

import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { Frequency } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Frequencies";
import { Route } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Routes";
import { Unit } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Unit";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { Popup } from "@/Common/Components/Modals/Popup";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DateTime } from "@/Common/Helpers/DateTime";
import { DosageDurationOrQuantityEditForm } from "@/Patient/Components/Medication/DosageDurationOrQuantityEditForm";
import { DosageEditForm } from "@/Patient/Components/Medication/DosageEditForm";
import { MedicationEditForm } from "@/Patient/Components/Medication/MedicationEditForm";
import { MedicalItemMedicationAndPrescriptionDosage } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescriptionDosage";
import { MedicationDosageDisplayMode, MedicationDosageDisplayModeDurationUnit, MedicationModel } from "@/Patient/Models/Medication/MedicationModel";

import styles from "./PrescriptionRenewalsViewEditMedicationPopup.module.scss";

interface IPrescriptionRenewalsViewEditMedicationPopupProps {
    title: string;
    initialMedicationModel: MedicationModel;
    onCancel: () => void;
    onSave: (updatedMedicationModel: MedicationModel) => void;
}

export function PrescriptionRenewalsViewEditMedicationPopup(props: IPrescriptionRenewalsViewEditMedicationPopupProps): JSX.Element {
    const { getText } = useTranslation();

    const routes: Immutable.List<ComboBoxDropDownItem> = Immutable.List(cacheStore?.RoutesCache.map(p => Route.ToComboboxItem(p)) ?? []);
    const dosageTypes: Immutable.List<FlatLookupView> = Immutable.List(cacheStore?.FindLookupsByTypeCode("DosageType").map(p => p) ?? []);
    const frequencies: Immutable.List<ComboBoxDropDownItem> = Immutable.List(cacheStore?.FrequenciesCache.map(p => Frequency.ToComboboxItem(p)) ?? []);
    const dosageUnits: Immutable.List<ComboBoxDropDownItem> = Immutable.List(cacheStore?.UnitsCache.map(p => Unit.toComboboxItem(p)) ?? []);

    const [medicationModel, setMedicationModel] = useState<MedicationModel>(props.initialMedicationModel.Clone(false, false, true));
    const [isDurationManuallyChangedByDosageId, setIsDurationManuallyChangedByDosageId] = useState<{ [id: string]: boolean }>({});
    const [isLongTermManuallyChanged, setIsLongTermManuallyChanged] = useState<boolean>(false);

    const estimateEndDate: Date = medicationModel.EffectiveEndDate() ? medicationModel.EffectiveEndDate() : DateTime.nowDeprecated();
    const totalDurationNumberOfDays: number = medicationModel.TicksToDays(medicationModel.Model.TotalDurationManual);
    const lastDosage = medicationModel.GetLastDosage();
    const dosageCount = medicationModel.Model.Dosages?.count();
    const numberOfRefill = IsNullOrUndefined(medicationModel.Model.NumberOfRefill) ? "" : medicationModel.Model.NumberOfRefill.toString();
    const requiresTotalDuration = true;// (medicationModel.Model.Dosages == null) || !medicationModel.Model.Dosages.every((x) => x.DisplayMode != MedicationDosageDisplayMode.Quantity) || medicationModel.Model.Dosages.count() > 1;
    const shouldShowSingleDosageQuantityInsteadOfDuration = MedicationModel.shouldShowSingleDosageQuantityInsteadOfDuration(dosageCount, lastDosage.DisplayMode);

    const getMedicationWithUpdatedDurationMode = (dosageId: string, mode: MedicationDosageDisplayMode, unit: MedicationDosageDisplayModeDurationUnit): MedicationModel => {
        const clone = medicationModel.Clone(false, false, true);

        clone.SetModeForDosage(dosageId, mode, unit);

        return clone;
    };

    const getMedicationWithUpdatedDosageDuration = (dosageId: string, newText: string, unit: MedicationDosageDisplayModeDurationUnit = null): MedicationModel => {
        const clone = medicationModel.Clone(false, false, true);
        const valueInDays = MedicalItemMedicationAndPrescriptionDosage.getDurationValueInDays(newText, unit);

        clone.Model.Dosages = clone.Model.Dosages.map(p => {
            if (p.Id === dosageId) {
                p.Duration = clone.DaysToTicks(valueInDays);
            }

            return p;
        }).toList();

        return clone;
    };

    const getMedicationWithUpdatedDosageQuantity = (dosageId: string, newText: string): MedicationModel => {
        const clone = medicationModel.Clone(false, false, true);

        clone.Model.Dosages = clone.Model.Dosages.map(p => {
            if (p.Id === dosageId) {
                p.DosageServedQuantity = newText;
            }

            return p;
        }).toList();

        return clone;
    };

    const getMedicationWithCalculatedQuantityAndDuration = (medicationModel: MedicationModel): MedicationModel => {
        const clone = medicationModel.Clone(false, false, true);

        clone.recalculateDispensationQuantities();
        clone.RecalculateTotalDuration();

        return clone;
    };

    const updateLongTerm = (isLongTerm: boolean, medicationModel: MedicationModel, isManualChange: boolean): MedicationModel => {
        if (!cacheStore.ClinicSettings.EnableLongTermMedication)
            return medicationModel;

        const clone = medicationModel.Clone(false, false, true);
        clone.Model.IsLongTerm = isLongTerm;

        if (isManualChange)
            setIsLongTermManuallyChanged(true);

        if (
            !settingsStore.configuration.get("LongTermMedicationDurationInDays")
            || !isManualChange
            || medicationModel.IsNarcotic()
            || medicationModel.Model.IsShortTerm
        )
            return clone;

        // Automatically update duration for all dosages
        clone.Model.Dosages?.forEach(dosage => {
            if (isDurationManuallyChangedByDosageId[dosage.Id] || dosage.DisplayMode === MedicationDosageDisplayMode.Quantity)
                return;

            dosage.Duration = isLongTerm
                ? medicationModel.DaysToTicks(settingsStore.configuration.get("LongTermMedicationDurationInDays"))
                : props.initialMedicationModel.GetDosageById(dosage.Id).Duration; // restore initial value
        });

        return clone;
    };

    const renderDosageEditForm = (): JSX.Element[] => {
        const renderingElements: JSX.Element[] = [];
        const dosages = medicationModel?.Model?.Dosages?.toArray() ?? [];
        const isNarcotic = medicationModel.IsNarcotic();
        const lastDosageIndex = medicationModel.Model.Dosages.count() - 1;

        for (let i = 0; i < dosages.length; i++) {
            const currentDosage = dosages[i];
            const selectedDosageTypeId: string = MedicalItemMedicationAndPrescriptionDosage.getSelectedDosageTypeId(currentDosage.DosageTypeLookupId);
            const selectedDosageUnitId = currentDosage.DosageUnit?.toString();
            const numberofDays: number = medicationModel.TicksToDays(currentDosage.Duration);

            renderingElements.push(
                <DosageEditForm
                    key={currentDosage.Id + medicationModel.Model.Id}
                    medicationModelModelId={medicationModel.Model.Id}
                    currentDosageId={currentDosage.Id}
                    currentDosageString={currentDosage.DosageString}
                    currentDosageFrequency={currentDosage.DosageFrequency}
                    currentDosageIfNeeded={currentDosage.IfNeeded}
                    currentDosageServedFrequency={currentDosage.DosageServedFrequency}
                    currentDosageServedDays={currentDosage.DosageServedDays}
                    isNarcotic={medicationModel.IsNarcotic()}
                    numberOfDays={medicationModel.TicksToDays(currentDosage.Duration)}
                    isFirstDosage={i === 0}
                    isLastDosage={i === lastDosageIndex}
                    isFakeDosage={currentDosage.IsFakeDosage}
                    selectedDosageTypeId={selectedDosageTypeId}
                    selectedDosageUnitId={selectedDosageUnitId}
                    dosageTypes={dosageTypes}
                    frequencies={frequencies}
                    dosageUnits={dosageUnits}
                    dosageStringTextboxCss={styles.customDosageStringTextbox}
                    onDosageStringChange={(newText: string): void => {
                        const clone = medicationModel.Clone(false, false, true);

                        clone.Model.Dosages = clone.Model.Dosages.map(p => {
                            if (p.Id === currentDosage.Id) {
                                p.DosageString = newText;
                            }

                            return p;
                        }).toList();

                        const cloneWithCalculatedQuantityAndDuration = getMedicationWithCalculatedQuantityAndDuration(clone);
                        setMedicationModel(cloneWithCalculatedQuantityAndDuration);
                    }}
                    onDosageUnitChange={(selectedCode: string): void => {
                        const clone = medicationModel.Clone(false, false, true);

                        clone.Model.Dosages = clone.Model.Dosages.map(p => {
                            if (p.Id === currentDosage.Id) {
                                p.DosageUnit = Number(selectedCode);
                            }

                            return p;
                        }).toList();

                        clone.recalculateDispensationQuantities();
                        setMedicationModel(clone);
                    }}
                    onDosageTypeChange={(selectedLookupId: string): void => {
                        const clone = medicationModel.Clone(false, false, true);

                        clone.Model.Dosages = clone.Model.Dosages.map(p => {
                            if (p.Id === currentDosage.Id) {
                                p.DosageTypeLookupId = selectedLookupId;
                            }

                            return p;
                        }).toList();

                        clone.recalculateDispensationQuantities();
                        setMedicationModel(clone);
                    }}
                    onDosageFrequencyChange={(selectedCode: string): void => {
                        const clone = medicationModel.Clone(false, false, true);

                        if (clone.GetDosageById(currentDosage.Id).DosageFrequency != selectedCode) {
                            clone.Model.Dosages = clone.Model.Dosages.map(p => {
                                if (p.Id === currentDosage.Id) {
                                    p.DosageFrequency = selectedCode;
                                }

                                return p;
                            }).toList();

                            clone.RecalculateTotalDuration();
                        }

                        clone.recalculateDispensationQuantities();
                        setMedicationModel(clone);
                    }}
                    onDosageIfNeededChange={(isChecked: boolean): void => {
                        const clone = medicationModel.Clone(false, false, true);

                        clone.Model.Dosages = clone.Model.Dosages.map(p => {
                            if (p.Id === currentDosage.Id) {
                                p.IfNeeded = isChecked;
                            }

                            return p;
                        }).toList();

                        setMedicationModel(clone);
                    }}
                    onDosageServedFrequencyChange={(newText: string): void => {
                        const clone = medicationModel.Clone(false, false, true);

                        clone.Model.Dosages = clone.Model.Dosages.map(p => {
                            if (p.Id === currentDosage.Id) {
                                p.DosageServedFrequency = newText;
                            }

                            return p;
                        }).toList();

                        setMedicationModel(clone);
                    }}
                    onDosageServedDaysChange={(newText: string): void => {
                        const clone = medicationModel.Clone(false, false, true);

                        clone.Model.Dosages = clone.Model.Dosages.map(p => {
                            if (p.Id === currentDosage.Id) {
                                p.DosageServedDays = newText;
                            }

                            return p;
                        }).toList();

                        setMedicationModel(clone);
                    }}
                    onDeleteDosageClick={(): void => {
                        const clone = medicationModel.Clone(false, false, true);

                        clone.DeleteDosage(currentDosage.Id);
                        clone.recalculateDispensationQuantities();

                        setMedicationModel(clone);
                    }}
                >
                    <DosageDurationOrQuantityEditForm
                        medicalItemId={medicationModel.Model.Id}
                        currentDosageId={currentDosage.Id}
                        currentDosageDisplayMode={currentDosage.DisplayMode}
                        currentDosageDisplayModeDurationUnit={currentDosage.DisplayModeDurationUnit}
                        currentDosageNarcoticServedQuantity={currentDosage.DosageServedQuantity ?? ""}
                        numberOfDays={numberofDays}
                        isNarcotic={isNarcotic}
                        onDosageDurationDaysClick={(): void => {
                            const clone = getMedicationWithUpdatedDurationMode(currentDosage.Id, MedicationDosageDisplayMode.Duration, MedicationDosageDisplayModeDurationUnit.Days);

                            setMedicationModel(clone);
                        }}
                        onDosageDurationWeeksClick={(): void => {
                            const clone = getMedicationWithUpdatedDurationMode(currentDosage.Id, MedicationDosageDisplayMode.Duration, MedicationDosageDisplayModeDurationUnit.Weeks);

                            setMedicationModel(clone);
                        }}
                        onDosageDurationMonthsClick={(): void => {
                            const clone = getMedicationWithUpdatedDurationMode(currentDosage.Id, MedicationDosageDisplayMode.Duration, MedicationDosageDisplayModeDurationUnit.Months);

                            setMedicationModel(clone);
                        }}
                        onDosageQuantityClick={(): void => {
                            const clone = getMedicationWithUpdatedDurationMode(currentDosage.Id, MedicationDosageDisplayMode.Quantity, null);

                            setMedicationModel(clone);
                        }}
                        onDosageDurationChange={(newText: string): void => {
                            let clone = getMedicationWithUpdatedDosageDuration(currentDosage.Id, newText, currentDosage.DisplayModeDurationUnit);
                            clone = getMedicationWithCalculatedQuantityAndDuration(clone);
                            setIsDurationManuallyChangedByDosageId(previous => ({ ...previous, [currentDosage.Id]: true }));

                            if (
                                !isLongTermManuallyChanged
                                && settingsStore.configuration.get("LongTermMedicationDurationInDays")
                                && !props.initialMedicationModel.Model.IsShortTerm
                            ) {
                                const durationInDays = MedicalItemMedicationAndPrescriptionDosage.getDurationValueInDays(newText, currentDosage.DisplayModeDurationUnit);

                                updateLongTerm(
                                    durationInDays === settingsStore.configuration.get("LongTermMedicationDurationInDays") || props.initialMedicationModel.Model.IsLongTerm,
                                    medicationModel,
                                    false
                                );
                            }

                            setMedicationModel(clone);
                        }}
                        onDosageQuantityChange={(newText: string): void => {
                            let clone = getMedicationWithUpdatedDosageQuantity(currentDosage.Id, newText);
                            clone = getMedicationWithCalculatedQuantityAndDuration(clone);

                            setMedicationModel(clone);
                        }}
                    />
                </DosageEditForm>
            );
        }

        return renderingElements;
    };

    return (
        <Popup
            title={props.title}
            onCloseDelegate={() => props.onCancel()}
            standardFooterProps={{
                cancelButton: {
                    label: getText("CancelLabel"),
                    className: "btn btn-default push-right-5",
                    onClick: () => props.onCancel()
                },
                okButton: {
                    label: getText("SaveLabel"),
                    className: "btn btn-primary",
                    onClick: () => {
                        props.onSave(medicationModel);
                    },
                    dataCy: "prescriptionRenewals-editMedicationPopup-saveButton"
                }
            }}
            cssClass={styles.popupContainer}
            centeredHorizontally
            manageOverlay
            draggable
            isOverflowVisible
            titleLowerCase
        >
            <div className={styles.container}>
                <div className={styles.modification}>
                    <MedicationEditForm
                        medicationModel={medicationModel}
                        routes={routes}
                        totalDurationNumberOfDays={totalDurationNumberOfDays}
                        estimateEndDate={estimateEndDate}
                        numberOfRefill={numberOfRefill}
                        requiresTotalDuration={requiresTotalDuration}
                        currentIdx={-1}
                        onMedicationNameChange={newText => {
                            const clone = medicationModel.Clone(false, false, true);

                            clone.Model.Name = newText;

                            setMedicationModel(clone);
                        }}
                        onLongTermChange={isChecked => setMedicationModel(updateLongTerm(isChecked, medicationModel, true))}
                        onDoNotSubstituteChange={(isChecked: boolean): void => {
                            const clone = medicationModel.Clone(false, false, true);

                            clone.Model.IsSubstituable = !isChecked;

                            setMedicationModel(clone);
                        }}
                        onRouteChange={selectedCode => {
                            const clone = medicationModel.Clone(false, false, true);

                            clone.Model.Route = selectedCode;

                            setMedicationModel(clone);
                        }}
                        onInstructionChange={newText => {
                            const clone = medicationModel.Clone(false, false, true);

                            clone.Model.Instruction = newText;

                            setMedicationModel(clone);
                        }}
                        onAddDosageClick={(): void => {
                            const clone = medicationModel.Clone(false, false, true);

                            clone.AddNewDosage();
                            clone.recalculateDispensationQuantities();

                            setMedicationModel(clone);
                        }}
                        onInitialRefillQuantityChange={newText => {
                            const clone = medicationModel.Clone(false, false, true);

                            clone.Model.InitialRefillQuantity = newText;

                            setMedicationModel(clone);
                        }}
                        onQuantityOrDurationChange={newText => {
                            const firstDosageId = medicationModel.Model.Dosages.first().Id;
                            const clone = shouldShowSingleDosageQuantityInsteadOfDuration ?
                                getMedicationWithUpdatedDosageQuantity(firstDosageId, newText) :
                                getMedicationWithUpdatedDosageDuration(firstDosageId, newText);

                            setMedicationModel(clone);
                        }}
                        onRenewalChange={newText => {
                            const clone = medicationModel.Clone(false, false, true);

                            clone.Model.NumberOfRefill = Number(newText);

                            clone.RecalculateTotalDuration();
                            setMedicationModel(clone);
                        }}
                        onRefillQuantityChange={newText => {
                            const clone = medicationModel.Clone(false, false, true);

                            clone.Model.RefillQuantity = newText;

                            setMedicationModel(clone);
                        }}
                        onTotalDurationChange={newText => {
                            const clone = medicationModel.Clone(false, false, true);

                            clone.Model.TotalDurationManual = clone.DaysToTicks(newText);
                            clone.RecalculateRefillEndDate();

                            setMedicationModel(clone);
                        }}
                        onStartDateChange={(startDate, startDatePrecision) => {
                            const clone = medicationModel.Clone(false, false, true);

                            clone.Model.RefillStartDate = startDate;
                            clone.Model.StartDatePrecision = startDatePrecision;

                            setMedicationModel(clone);
                        }}
                    >
                        {renderDosageEditForm()}
                    </MedicationEditForm>
                </div>
            </div>
        </Popup>
    );
}
